/**
 * Logger type definitions
 * 
 * Provides structured logging with environment-aware output
 */

import { JSONValue } from '@/types/common';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogContext {
  userId?: string;
  companyId?: string;
  personaId?: string;
  analysisId?: string;
  function?: string;
  component?: string;
  duration?: number;
  error?: Error | string;
  metadata?: Record<string, JSONValue>;
  data?: JSONValue;
  [key: string]: JSONValue | Error | undefined;
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: LogContext;
}

export interface LoggerConfig {
  isDevelopment: boolean;
  logLevel: LogLevel;
  enableTimestamps: boolean;
  enableColors: boolean;
}

export interface Logger {
  debug(message: string, context?: LogContext): void;
  info(message: string, context?: LogContext): void;
  warn(message: string, context?: LogContext): void;
  error(message: string, error?: Error | string, context?: LogContext): void;
  time(label: string): void;
  timeEnd(label: string): number | undefined;
  setLogLevel(level: LogLevel): void;
  getLogLevel(): LogLevel;
}

// Log level priorities for filtering
export const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
};

// Console colors for different log levels (ANSI escape codes)
export const LOG_COLORS = {
  debug: '\x1b[36m', // Cyan
  info: '\x1b[32m',  // Green
  warn: '\x1b[33m',  // Yellow
  error: '\x1b[31m', // Red
  reset: '\x1b[0m'   // Reset
} as const;